/**
 * 注册终端 —— 真的调 hub 的 POST /devices,拿它返回的令牌。
 *
 * 原型这里是**前端自己 Math.random 编一个 `hsk_…` 字符串**当令牌显示。
 * 那不只是假数据:照着它去配一台狗,狗永远登不上,而台面上看起来一切正常。
 * 令牌只能由 hub 生成(它只存 sha256,明文只返回这一次)。
 */
import { useState } from 'react';
import { typeIcons } from '@/lib/terminalLabels';
import { hub, HubError } from '@/lib/hubClient';

// kind 用 hub 认的那几个(devices.Kind*);vehicle 走 other —— 注册表还没有这一类。
const allTypes = [
  { id: 'dog', label: '机器狗' },
  { id: 'glasses', label: 'AI眼镜' },
  { id: 'phone', label: '手机' },
  { id: 'drone', label: '无人机' },
  { id: 'other', label: '其它' },
] as const;

interface RegisterDeviceModalProps { onClose: () => void; onRegistered?: () => void; }

export default function RegisterDeviceModal({ onClose, onRegistered }: RegisterDeviceModalProps) {
  const [step, setStep] = useState<'form' | 'token'>('form');
  const [name, setName] = useState('');
  const [id, setId] = useState('');
  const [kind, setKind] = useState<string>('');
  const [token, setToken] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleRegister = async () => {
    if (!name.trim() || !kind || busy) return;
    setBusy(true); setError('');
    try {
      const r = await hub.registerDevice({ id: id.trim(), name: name.trim(), kind });
      setToken(r.token);
      setDeviceId(r.device.id);
      setStep('token');
      onRegistered?.();
    } catch (e) {
      setError(e instanceof HubError && e.status === 403
        ? '仅座舱(admin 令牌)可注册设备 —— 当前登录的是设备身份'
        : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setStep('form'); setToken(''); setName(''); setId(''); setKind(''); setError(''); };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-foreground-950/40" onClick={onClose}></div>
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[480px] max-w-[92vw] max-h-[88vh] overflow-y-auto bg-background-50 border border-background-200/70 rounded-xl shadow-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70">
          <h3 className="text-sm font-semibold text-foreground-800 font-heading">{step === 'form' ? '注册新终端' : '接入令牌'}</h3>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded hover:bg-background-100 cursor-pointer"><i className="ri-close-line text-foreground-500"></i></button>
        </div>
        <div className="px-5 py-4">
          {step === 'form' && (<>
            <p className="text-xs text-foreground-500 mb-4">hub 会生成一把只属于这台设备的令牌,吊销它不影响别的终端。</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">终端名称</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="例如：小智" className="w-full px-3 py-2.5 text-sm border border-background-200/70 rounded-lg bg-background-50 focus:outline-none focus:ring-1 focus:ring-primary-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">终端标识 <span className="text-foreground-400 font-normal">(可留空,hub 按类型+名称生成)</span></label>
                <input type="text" value={id} onChange={(e) => setId(e.target.value)} placeholder="dog-01" className="w-full px-3 py-2.5 text-sm font-mono border border-background-200/70 rounded-lg bg-background-50 focus:outline-none focus:ring-1 focus:ring-primary-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">终端类型</label>
                <div className="grid grid-cols-3 gap-2">{allTypes.map((t) => (
                  <button key={t.id} onClick={() => setKind(t.id)} className={`flex flex-col items-center gap-1 px-2 py-3 rounded-lg border text-xs transition-all cursor-pointer whitespace-nowrap ${kind === t.id ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-background-200/70 text-foreground-600 hover:bg-background-100'}`}>
                    <span className="w-5 h-5 flex items-center justify-center"><i className={`${typeIcons[t.id] ?? 'ri-device-line'} text-sm`}></i></span>{t.label}
                  </button>))}
                </div>
              </div>
            </div>
            {error && <p className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200/70 rounded-lg px-3 py-2">{error}</p>}
            <div className="mt-5 flex gap-2">
              <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-medium text-foreground-600 border border-background-200/70 rounded-lg hover:bg-background-100 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={handleRegister} disabled={!name.trim() || !kind || busy} className="flex-1 px-4 py-2.5 text-sm font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">{busy ? '注册中…' : '注册并生成令牌'}</button>
            </div>
          </>)}
          {step === 'token' && (<>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
              <div className="flex items-start gap-2">
                <span className="w-4 h-4 flex items-center justify-center mt-0.5"><i className="ri-error-warning-fill text-red-500 text-sm"></i></span>
                <p className="text-xs text-red-700"><span className="font-semibold">请立即复制并妥善保存</span><br />hub 只存令牌的 sha256,明文仅此一次。丢了只能 rotate 换一把。</p>
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-xs font-medium text-foreground-700 mb-1">设备标识</label>
              <div className="bg-background-100 border border-background-200/70 rounded-lg px-3 py-2 font-mono text-xs text-foreground-700">{deviceId}</div>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium text-foreground-700 mb-1">接入令牌</label>
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0 bg-background-100 border border-background-200/70 rounded-lg px-3 py-2.5 font-mono text-xs text-foreground-700 break-all">{token}</div>
                <button onClick={() => { navigator.clipboard.writeText(token); setCopied(true); setTimeout(() => setCopied(false), 2000); }} className="px-3 py-2.5 bg-primary-500 text-white text-xs font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">{copied ? '已复制' : '复制'}</button>
              </div>
              <p className="text-[11px] text-foreground-400 mt-2">填到设备的 <span className="font-mono">auth.env</span>(狗:<span className="font-mono">OXMAN_HUB_TOKEN</span>),或引导脚本 <span className="font-mono">/install.sh</span> 的参数里。</p>
            </div>
            <div className="flex gap-2">
              <button onClick={reset} className="flex-1 px-4 py-2.5 text-sm font-medium text-foreground-600 border border-background-200/70 rounded-lg hover:bg-background-100 cursor-pointer whitespace-nowrap">再注册一个</button>
              <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600 cursor-pointer whitespace-nowrap">完成</button>
            </div>
          </>)}
        </div>
      </div>
    </>
  );
}
